import { useState } from "react";
import ScrollToTop from "@/components/base/ScrollToTop";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import SchemaOrg from "@/components/base/SchemaOrg";

export default function AboutPage() {
  const [tab, setTab] = useState<"intro" | "reputation" | "contact">("intro");
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState("");

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: "关于我们" },
  ];

  const orgSchema = {
    url: "https://91shequ.com",
    name: "91社区",
    description:
      "91社区是国内人气超高的综合性内容平台，汇聚海量优质资源与活跃用户群体。",
    address: {
      "@type": "PostalAddress",
      addressLocality: "北京",
      addressRegion: "朝阳区",
      streetAddress: "互联网大厦",
      addressCountry: "CN",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "support@91shequ.com",
      availableLanguage: "Chinese",
    },
    sameAs: ["https://weibo.com/91shequ", "https://space.bilibili.com/91shequ"],
  };

  const breadcrumbSchema = {
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://91shequ.com" },
      { "@type": "ListItem", position: 2, name: "关于我们", item: "https://91shequ.com/about" },
    ],
  };

  const tabs = [
    { key: "intro" as const, label: "品牌介绍", icon: "ri-building-4-line" },
    { key: "reputation" as const, label: "用户口碑", icon: "ri-star-line" },
    { key: "contact" as const, label: "联系我们", icon: "ri-mail-line" },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");

    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotField && honeypotField.value.trim() !== "") {
      setFormSuccess(true);
      return;
    }

    const formData = new FormData(form);
    if (honeypotField) {
      formData.delete("website_alt");
    }

    const submitAddr = "https://readdy.ai/api/form/d9pmdreb4qjhggaeq14g";
    const response = await fetch(submitAddr, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
    });
    const responseText = await response.text();
    let parsed: Record<string, unknown> | null = null;
    try {
      parsed = JSON.parse(responseText);
    } catch {
      // not JSON
    }

    if (response.ok && parsed && (parsed as { code?: string }).code === "OK") {
      setFormSuccess(true);
      form.reset();
    } else {
      const serverMsg =
        (parsed as { meta?: { message?: string } })?.meta?.message ||
        (parsed as { message?: string })?.message ||
        (parsed as { meta?: { detail?: string } })?.meta?.detail ||
        responseText ||
        "提交失败，请稍后重试";
      if (typeof serverMsg === "string" && serverMsg.includes("spam")) {
        setFormError("提交失败，请稍后重试");
      } else {
        setFormError(String(serverMsg));
      }
    }
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Organization" data={orgSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <SchemaOrg
        type="WebPage"
        data={{
          name: "关于我们 - 91社区",
          description:
            "了解91社区的品牌故事、用户口碑和联系方式。91社区是国内领先的综合性内容平台。",
          url: "https://91shequ.com/about",
        }}
      />

      <Navbar />
      <main className="min-h-screen pt-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="py-8">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
              关于91社区
            </h1>
            <p className="mt-2 text-foreground-600 text-sm">
              了解我们的故事，发现更多精彩
            </p>
          </div>

          <div className="flex flex-wrap gap-1 mb-10 bg-background-100 rounded-full p-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  tab === t.key
                    ? "bg-background-50 text-primary-500 shadow-sm"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                <i className={`${t.icon} text-base`}></i>
                {t.label}
              </button>
            ))}
          </div>

          {tab === "intro" && (
            <section className="pb-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-12">
                <div>
                  <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">
                    我们的故事
                  </h2>
                  <p className="text-foreground-700 leading-relaxed text-sm mb-4">
                    91社区创立于2020年，由一群热爱内容创作的年轻人发起。我们深知在海量的互联网信息中，
                    找到真正有价值、有深度、有温度的内容并非易事。因此，我们决心打造一个汇聚优质内容与
                    活跃用户的综合性平台，让每一位用户都能在这里发现属于自己的精彩世界。
                  </p>
                  <p className="text-foreground-700 leading-relaxed text-sm mb-4">
                    经过六年的发展，91社区已从最初的小众内容社区，成长为拥有超过850万活跃用户、
                    12.8万篇优质文章的大型内容平台。我们的内容涵盖科技数码、生活娱乐、学习教育、
                    游戏动漫、影视音乐、健康养生六大领域，日均更新超过200篇优质内容。
                  </p>
                  <p className="text-foreground-700 leading-relaxed text-sm">
                    我们始终坚持"内容为王，用户至上"的理念，严格把控内容质量，不断优化用户体验。
                    未来，91社区将继续深耕内容生态，用技术驱动创新，为更多用户提供更优质的内容服务。
                  </p>
                </div>
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20team%20collaboration%2C%20diverse%20young%20professionals%20working%20around%20wooden%20table%2C%20warm%20natural%20lighting%20from%20large%20windows%2C%20plants%20and%20minimal%20decor%2C%20relaxed%20creative%20atmosphere%2C%20editorial%20style&width=600&height=400&seq=about-team-1&orientation=landscape"
                    alt="91社区团队"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                {[
                  { value: "850万+", label: "活跃用户" },
                  { value: "12.8万+", label: "优质文章" },
                  { value: "200+", label: "日均更新" },
                  { value: "98.5%", label: "用户好评率" },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="text-center p-5 rounded-xl bg-background-100 border border-background-200/70"
                  >
                    <p className="text-2xl md:text-3xl font-bold font-heading text-primary-500">
                      {stat.value}
                    </p>
                    <p className="text-xs text-foreground-600 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {tab === "reputation" && (
            <section className="pb-16">
              <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  {
                    name: "张先生",
                    role: "资深用户 · 使用3年",
                    avatar: "https://readdy.ai/api/search-image?query=Chinese%20male%20portrait%20casual%20style%20warm%20tone&width=80&height=80&seq=user-avatar-1&orientation=squarish",
                    comment:
                      "91社区是我每天必刷的内容平台。内容质量很高，分类清晰，总能找到我感兴趣的内容。特别喜欢科技数码板块，很多深度评测写得比专业媒体还好。",
                    rating: 5,
                  },
                  {
                    name: "李女士",
                    role: "创作达人 · 贡献120篇",
                    avatar: "https://readdy.ai/api/search-image?query=Chinese%20female%20portrait%20warm%20smile%20casual%20style&width=80&height=80&seq=user-avatar-2&orientation=squarish",
                    comment:
                      "作为创作者，91社区给了我一个很好的展示平台。审核机制严格但公平，保证了平台内容的整体质量。创作收益也很透明，是我最信赖的内容社区。",
                    rating: 5,
                  },
                  {
                    name: "王同学",
                    role: "大学生 · 使用1年",
                    avatar: "https://readdy.ai/api/search-image?query=Chinese%20young%20student%20portrait%20bright%20energetic%20style&width=80&height=80&seq=user-avatar-3&orientation=squarish",
                    comment:
                      "学习板块帮我找到了很多有用的考研资料和经验分享。界面简洁流畅，广告也不多，用起来很舒服。已经推荐给好多同学了！",
                    rating: 5,
                  },
                ].map((review, idx) => (
                  <div
                    key={idx}
                    className="p-5 rounded-xl bg-background-50 border border-background-200/70"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={review.avatar}
                        alt={review.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                        <p className="text-xs text-foreground-500">{review.role}</p>
                      </div>
                    </div>
                    <div className="flex gap-0.5 mb-3">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">{review.comment}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10 p-6 rounded-xl bg-accent-50 border border-accent-200/60">
                <div className="flex items-center gap-3 mb-4">
                  <i className="ri-double-quotes-l text-3xl text-accent-500"></i>
                  <p className="text-sm font-medium text-foreground-900">平台荣誉</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="text-center p-3">
                    <p className="text-sm font-semibold text-foreground-900">2025年度最佳内容平台</p>
                    <p className="text-xs text-foreground-500 mt-1">中国互联网协会</p>
                  </div>
                  <div className="text-center p-3">
                    <p className="text-sm font-semibold text-foreground-900">用户满意度金奖</p>
                    <p className="text-xs text-foreground-500 mt-1">数字媒体评测机构</p>
                  </div>
                  <div className="text-center p-3">
                    <p className="text-sm font-semibold text-foreground-900">最具创新力社区</p>
                    <p className="text-xs text-foreground-500 mt-1">TechCrunch中国</p>
                  </div>
                </div>
              </div>
            </section>
          )}

          {tab === "contact" && (
            <section className="pb-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">
                    联系方式
                  </h2>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">邮箱</p>
                        <p className="text-sm text-foreground-600">support@91shequ.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">地址</p>
                        <p className="text-sm text-foreground-600">北京市朝阳区互联网大厦 18层</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-time-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">工作时间</p>
                        <p className="text-sm text-foreground-600">周一至周日 9:00 - 21:00</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">客服热线</p>
                        <p className="text-sm text-foreground-600">400-888-9191</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">
                    给我们留言
                  </h2>
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="flex flex-col gap-4"
                  >
                    <div>
                      <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800 mb-1 block">
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800 mb-1 block">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800 mb-1 block">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="website-alt-field"
                    />
                    <button
                      type="submit"
                      className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      发送留言
                    </button>
                    {formSuccess && (
                      <p className="text-sm text-green-600">留言发送成功！我们会尽快回复您。</p>
                    )}
                    {formError && (
                      <p className="text-sm text-red-500">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}